import { applyFrameOp } from "./frame-ops";
import type {
  AnimClip,
  AnimClipTable,
  AnimDir,
  AnimRecipesTable,
  AnimState,
  AuthoredAnimClip,
  AuthoredAnimTable,
  AuthoredDirectionalAnimTable,
  CardinalDir,
  SpriteTemplate,
  UnitAnimSet,
} from "./types";

const STATES: AnimState[] = ["idle", "walk", "attack", "death"];

function cloneClip(clip: AuthoredAnimClip | AnimClip): AnimClip {
  return {
    fps: clip.fps,
    loop: clip.loop,
    frames: clip.frames.map((rows) => rows.map((r) => r)),
  };
}

function authoredStatesToClips(
  states: Record<AnimState, AuthoredAnimClip>,
  spriteId: string,
): AnimClipTable {
  const out = {} as AnimClipTable;
  for (const state of STATES) {
    const clip = states[state];
    if (!clip) throw new Error(`Missing authored clip ${spriteId}.${state}`);
    if (!clip.frames.length) {
      throw new Error(`Empty authored frames ${spriteId}.${state}`);
    }
    out[state] = cloneClip(clip);
  }
  // Extra named clips (pattern anims like boss "slam"/"brass"/"leap") ride along.
  for (const [name, clip] of Object.entries(
    states as Record<string, AuthoredAnimClip>,
  )) {
    if ((STATES as string[]).includes(name)) continue;
    if (!clip?.frames?.length) {
      throw new Error(`Empty authored frames ${spriteId}.${name}`);
    }
    out[name] = cloneClip(clip);
  }
  return out;
}

/** Horizontal flip of every frame row (E → W). */
export function flipClipHorizontal(clip: AnimClip): AnimClip {
  return {
    fps: clip.fps,
    loop: clip.loop,
    frames: clip.frames.map((rows) =>
      rows.map((row) => [...row].reverse().join("")),
    ),
  };
}

export function flipStatesHorizontal(states: AnimClipTable): AnimClipTable {
  const out = {} as AnimClipTable;
  for (const [name, clip] of Object.entries(states)) {
    out[name] = flipClipHorizontal(clip);
  }
  return out;
}

/** Build UnitAnimSet from base sprite + shared recipes (engine-side expand). */
export function buildAnimSet(
  base: SpriteTemplate,
  recipes: AnimRecipesTable,
): UnitAnimSet {
  const states = {} as Record<AnimState, AnimClip>;
  for (const state of STATES) {
    const recipe = recipes.states[state];
    if (!recipe) throw new Error(`Missing anim recipe for ${state}`);
    states[state] = {
      fps: recipe.fps,
      loop: recipe.loop,
      frames: recipe.frames.map((ref) => applyFrameOp(base, ref)),
    };
  }
  return { id: base.id, dir: recipes.dir, states };
}

/** Load hand-authored per-state frame grids (no frame-ops). */
export function loadAuthoredAnimSet(
  spriteId: string,
  table: AuthoredAnimTable,
): UnitAnimSet {
  return {
    id: spriteId,
    dir: table.dir,
    states: authoredStatesToClips(table.states, spriteId),
  };
}

/**
 * Load E/N/S authored dirs and synthesize W = flip(E).
 * Default `states` / `dir` = east.
 */
export function loadAuthoredDirectionalAnimSet(
  spriteId: string,
  table: AuthoredDirectionalAnimTable,
): UnitAnimSet {
  const e = authoredStatesToClips(table.dirs.e, `${spriteId}.e`);
  const n = authoredStatesToClips(table.dirs.n, `${spriteId}.n`);
  const s = authoredStatesToClips(table.dirs.s, `${spriteId}.s`);
  const w = flipStatesHorizontal(e);
  const dirs: Record<CardinalDir, Record<AnimState, AnimClip>> = { e, n, s, w };
  return {
    id: spriteId,
    dir: "e",
    states: e,
    dirs,
  };
}

/**
 * Non-combat props: repeat the base silhouette for every state
 * (satisfies min frame counts without procedural motion).
 */
export function singleFrameAnimSet(
  base: SpriteTemplate,
  copies = 2,
): UnitAnimSet {
  const frame = base.rows.map((r) => r);
  const frames = Array.from({ length: Math.max(1, copies) }, () =>
    frame.map((r) => r),
  );
  const clip = (fps: number, loop: boolean): AnimClip => ({
    fps,
    loop,
    frames: frames.map((f) => f.map((r) => r)),
  });
  return {
    id: base.id,
    dir: "side",
    states: {
      idle: clip(1, true),
      walk: clip(1, true),
      attack: clip(1, false),
      death: clip(1, false),
    },
  };
}

export function frameAt(
  clip: AnimClip,
  timeSec: number,
): { frameIndex: number; rows: string[] } {
  const n = clip.frames.length;
  if (n === 0) throw new Error("empty clip");
  const idx = Math.floor(timeSec * clip.fps);
  const frameIndex = clip.loop
    ? ((idx % n) + n) % n
    : Math.min(n - 1, Math.max(0, idx));
  return { frameIndex, rows: clip.frames[frameIndex]! };
}

export function clipDuration(clip: AnimClip): number {
  if (clip.loop) return Number.POSITIVE_INFINITY;
  return clip.frames.length / Math.max(0.01, clip.fps);
}

/** Resolve clip table for a facing (falls back to default states). */
export function statesForDir(
  anim: UnitAnimSet,
  facing?: AnimDir,
): AnimClipTable {
  if (facing && facing !== "side" && anim.dirs?.[facing as CardinalDir]) {
    return anim.dirs[facing as CardinalDir]!;
  }
  return anim.states;
}
